import { Component, OnInit, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { MatCardModule } from "@angular/material/card";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatChipsModule } from "@angular/material/chips";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar } from "@angular/material/snack-bar";
import { firstValueFrom } from "rxjs";

import { RespondentSurvey } from "../../../models/survey.model";
import { SurveyService } from "../../../services/survey.service";

@Component({
	selector: "app-surveys-list",
	standalone: true,
	imports: [
		CommonModule,
		RouterModule,
		MatCardModule,
		MatButtonModule,
		MatIconModule,
		MatChipsModule,
		MatProgressBarModule,
		MatProgressSpinnerModule,
	],
	templateUrl: "./surveys-list.component.html",
	styleUrls: ["./surveys-list.component.scss"],
})
export class SurveysListComponent implements OnInit {
	private surveyService = inject(SurveyService);
	private snackBar = inject(MatSnackBar);

	respondentSurveys = signal<RespondentSurvey[]>([]);
	loading = signal(true);

	ngOnInit(): void {
		void this.initialise();
	}

	private async initialise(): Promise<void> {
		await this.loadSurveys();
	}

	private async loadSurveys(): Promise<void> {
		try {
			this.loading.set(true);

			// Get active surveys for the current member
			const surveys = await firstValueFrom(this.surveyService.getActiveRespondentSurveys());
			this.respondentSurveys.set(surveys);
		} catch (error) {
			this.snackBar.open("Error loading questionnaires", "Close", { duration: 3000 });
			console.error("Error loading surveys:", error);
		} finally {
			this.loading.set(false);
		}
	}

	getStatusColor(status: string): string {
		switch (status) {
			case "completed":
				return "accent";
			case "in_progress":
				return "primary";
			case "pending":
				return "warn";
			default:
				return "primary";
		}
	}

	getStatusText(status: string): string {
		switch (status) {
			case "completed":
				return "Completed";
			case "in_progress":
				return "In Progress";
			case "pending":
				return "Pending";
			default:
				return status;
		}
	}

	// Helper method to handle Firestore timestamps
	formatDate(timestamp: any): Date | null {
		if (!timestamp) return null;

		// Check if it's a Firestore timestamp
		if (timestamp && typeof timestamp.toDate === "function") {
			return timestamp.toDate();
		}

		// If it's already a Date object
		if (timestamp instanceof Date) {
			return timestamp;
		}

		// If it's a string or number, try to create a Date
		try {
			return new Date(timestamp);
		} catch {
			return null;
		}
	}
}
